import { cloneDeep, isNil } from 'lodash';
import {
    createEmptyRowEntries,
    createEmptyTableEntries,
} from '../../../../../../common/table/utils/tableInitialisationUtils';
import { getColumnCount, getRowCount } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { CellSelection, CellSelections, TableContentData } from '../../../../../../common/table/TableTypes';
import { getMaxCellSelection } from '../../selectionUtils/tableSelectionGeometryUtils';
import { continueCellFormatting } from './tableGridFormattingContinuationUtils';

/**
 * Returns the final populated row index for a dense table content array.
 */
export const getFinalRowIndex = (data: TableContentData): number => getRowCount(data) - 1;

/**
 * Returns the final populated column index for a dense table content array.
 */
export const getFinalColumnIndex = (data: TableContentData): number => getColumnCount(data) - 1;

/**
 * Checks whether the current selections still fit inside the provided table data bounds.
 */
export const selectionExistsInData = (dataArray: TableContentData, cellSelections: CellSelections): boolean => {
    let maxRow: number | null = null;
    let maxCol: number | null = null;

    cellSelections?.forEach((cellSelection: CellSelection) => {
        const { row, col } = getMaxCellSelection(cellSelection);
        if (isNil(maxRow) || row > maxRow) maxRow = row;
        if (isNil(maxCol) || col > maxCol) maxCol = col;
    });

    return !isNil(maxRow) && !isNil(maxCol) && maxRow < getRowCount(dataArray) && maxCol < getColumnCount(dataArray);
};

/**
 * Moves a contiguous row or column segment to a final index in-place.
 */
export const shiftRowsOrColumns = <T>(movedDataIndices: Array<number>, array: Array<T>, finalIndex: number): void => {
    const moving = array.splice(movedDataIndices[0], movedDataIndices.length);

    array.splice(finalIndex, 0, ...moving);
};

/**
 * Resizes persisted table content to the requested row and column count.
 */
export const adjustTableContentDataSize = (
    dataArray: TableContentData,
    nRows: number,
    nCols: number,
): TableContentData => {
    const previousRowCount = getRowCount(dataArray);
    const previousColumnCount = getColumnCount(dataArray);

    const newDataArray = cloneDeep(dataArray);

    if (newDataArray.length < nRows) {
        newDataArray.push(...createEmptyTableEntries(nRows - newDataArray.length, nCols));
    }

    if (newDataArray.length > nRows) {
        newDataArray.splice(nRows, newDataArray.length - nRows);
    }

    newDataArray.forEach((row) => {
        if (row.length < nCols) {
            row.push(...createEmptyRowEntries(nCols - row.length));
        }

        if (row.length > nCols) {
            row.splice(nCols, row.length - nCols);
        }
    });

    return continueCellFormatting({
        dataArray: newDataArray,
        colIndex: previousColumnCount,
        nColsAdded: nCols - previousColumnCount,
        rowIndex: previousRowCount,
        nRowsAdded: nRows - previousRowCount,
    });
};
